import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { can as canCheck } from './permissions';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('vroia-token'));
  const [loading, setLoading] = useState(true);
  // True when the session expired mid-use — so the login screen can explain why.
  const [sessionExpired, setSessionExpired] = useState(false);

  // Set axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Global 401 handler: an expired/invalid token must cleanly log the user out
  // and surface the login screen — never leave them staring at empty pages that
  // look like lost data. (The login request itself 401s on bad credentials, so
  // it's excluded.)
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const cfg = error?.config || {};
        const url = cfg.url || '';
        if (status === 401 && !url.includes('/api/auth/login')) {
          localStorage.removeItem('vroia-token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
          setSessionExpired(true);
          return Promise.reject(error);
        }
        // Silently retry transient failures — a deploy restart window, a gateway
        // blip (502/503/504), a timeout, or a dropped connection — on idempotent
        // GET reads, so a few seconds of downtime never surfaces as "could not
        // load data". Only GETs are retried (never POST/PUT/DELETE), capped at 3.
        const isGet = (cfg.method || 'get').toLowerCase() === 'get';
        const transient = !error.response || [502, 503, 504].includes(status) || error.code === 'ECONNABORTED';
        if (isGet && transient && url.startsWith('/api')) {
          cfg.__retry = (cfg.__retry || 0) + 1;
          if (cfg.__retry <= 3) {
            return new Promise((r) => setTimeout(r, cfg.__retry * 1500)).then(() => axios(cfg));
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Verify token on mount
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get('/api/auth/me')
      .then(res => { setUser(res.data.user); setLoading(false); })
      .catch(() => { setToken(null); setUser(null); localStorage.removeItem('vroia-token'); setLoading(false); });
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post('/api/auth/login', { email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('vroia-token', t);
    // Set header immediately so the first API calls after login work
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    setToken(t);
    setUser(u);
    setSessionExpired(false);
    return u;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('vroia-token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const can = useCallback((key) => canCheck(user, key), [user]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, isAuthenticated: !!user, can, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
